import React, { useState } from 'react';
import { Box, Button, TextField, Typography, Container, Alert, CircularProgress, AppBar, Toolbar } from '@mui/material';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Google as GoogleIcon } from '@mui/icons-material';
import UserProfile from '../components/UserProfile';
import './Auth.css';

const LoginPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const { signIn, signInWithGoogle } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      await signIn(email, password);
      navigate('/');
    } catch (err: any) {
      setError(err.message || 'Failed to log in.');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    setGoogleLoading(true);
    setError(null);
    
    try {
      await signInWithGoogle();
      navigate('/');
    } catch (err: any) {
      // Handle popup closed by user gracefully - reset immediately
      if (err.code === 'auth/popup-closed-by-user' || 
          err.code === 'auth/cancelled-popup-request' ||
          err.code === 'auth/popup-blocked') {
        // User closed the popup, just reset state immediately without showing error
        setError(null);
        setGoogleLoading(false);
        return; // Exit early to prevent further processing
      } else {
        setError(err.message || 'Failed to log in with Google.');
        setGoogleLoading(false);
      }
    }
  };

  return (
    <>
      {/* Background pattern */}
      <div className="bg-pattern"></div>
      
      <Box 
        className="main-container"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          minHeight: '100vh'
        }}
      >
        <AppBar
          position="static"
          elevation={0}
          sx={{ 
            color: 'white',
            flexShrink: 0
          }}
        >
          <Toolbar>
            <Typography 
              variant="h5" 
              component="div" 
              sx={{
                fontFamily: 'Dancing Script, cursive',
                fontWeight: 700,
                fontSize: '30px',
                cursor: 'pointer',
                color: 'white',
                '&:hover': {
                  opacity: 0.8,
                },
                ml: 1,
              }}
              onClick={() => {
                window.location.href = '/';
              }}
            >
              <p style={{ margin: 10, fontFamily: 'Soria, serif', fontWeight: 400 }}>Auditio</p>
            </Typography>
            <Box sx={{ flexGrow: 1 }} />
            
            {/* User Profile Component */}
            <UserProfile />
          </Toolbar>
        </AppBar>

        <Box sx={{ 
          flex: 1, 
          display: 'flex', 
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: 0,
          overflow: 'auto'
        }}>
          <Container component="main" maxWidth="sm" className="auth-container">
            <Box className="auth-box">
              <Typography component="h1" variant="h5" className="auth-title">
                Login
              </Typography>
              {error && <Alert severity="error" sx={{ mt: 2, mb: 2, width: '100%' }}>{error}</Alert>}
              <Box component="form" onSubmit={handleSubmit} noValidate sx={{ mt: 1 }}>
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  id="email"
                  label="Email Address"
                  name="email"
                  autoComplete="email"
                  autoFocus
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="auth-input"
                />
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  name="password"
                  label="Password"
                  type="password"
                  id="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="auth-input"
                />
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  sx={{ mt: 3, mb: 2 }}
                  className="auth-button"
                  disabled={loading}
                >
                  {loading ? <CircularProgress size={24} color="inherit" /> : 'Login'}
                </Button>
                
                {/* Divider */}
                <Box sx={{ display: 'flex', alignItems: 'center', my: 2 }}>
                  <Box sx={{ flex: 1, height: '1px', backgroundColor: 'rgba(85, 203, 205, 0.2)' }} />
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      px: 2, 
                      color: 'var(--text-color)', 
                      opacity: 0.6,
                      fontFamily: 'Poppins, sans-serif',
                      fontSize: '0.875rem'
                    }}
                  >
                    OR
                  </Typography>
                  <Box sx={{ flex: 1, height: '1px', backgroundColor: 'rgba(85, 203, 205, 0.2)' }} />
                </Box>
                
                <Button
                  fullWidth
                  variant="contained"
                  startIcon={!googleLoading ? <GoogleIcon /> : null}
                  sx={{ 
                    mb: 2,
                    background: 'linear-gradient(135deg, #f9967e, #fcb9aa) !important',
                    color: 'white !important',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #fcb9aa, #f9967e) !important',
                    }
                  }}
                  className="auth-google-button"
                  onClick={handleGoogleLogin}
                  disabled={loading || googleLoading}
                >
                  {googleLoading ? <CircularProgress size={24} color="inherit" /> : 'Login with Google'}
                </Button>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
                  <Link to="/forgot-password" className="auth-link">
                    Forgot password?
                  </Link>
                  <Link to="/signup" className="auth-link">
                    Don't have an account? Sign Up
                  </Link>
                </Box>
              </Box>
            </Box>
          </Container>
        </Box>
      </Box>
    </>
  );
};

export default LoginPage;